import React, { useState, useRef, useEffect } from "react";
import { useDrag } from "@use-gesture/react";
import styled from "styled-components";
import useMedia from "src/hooks/useMedia";
import MicrophoneVisualizer from "../../components/MicrophoneVisualizer";
import { FiCrop,FiPlay,FiPause } from "react-icons/fi";
import Loader from "src/assets/icons/loader.svg"
import VideoAudioWaveform from "./VideoAudioWaveform";
import { theme } from "src/styles/theme";

interface VideoTrimmerProps {
  index: number;
  isActive: boolean;
  width: number;
  duration: number;
  video?: any;
  onTrimPlay?:(args:any)=>void;
  onTrimChange?: (times: { startTime?: number; endTime?: number }) => void;
  onVideoTap?: (index: number) => void;
  thumbnails: React.ReactNode;
}

const Wrapper = styled.div<{width: number}>`
  flex: 1;
  position: relative;
  // height: 170px;
  width: 50%;
  max-width: 50%;
  // border-radius: 4px;
  display: flex;
  flex-direction: column;
  border: 0px solid #e0e0e0;
  gap: 10px;
  padding: 8px 0px;

  @media (max-width: ${theme.breakpoints.maxTablet}px) {
    width: 100%;
    max-width: 100%;
  }

`;

const TrackItemHeader = styled.div`
  width: 100%;
  height: 35px;
  display: flex;
  z-index: 9999999999999;

  .tabButton{
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 35px;
    height: 35px;
    gap: 8px;
    border-radius: 10px;
    border-width: 1px;
    padding: 10px;
    border: 1px solid #0000001A;
    cursor: pointer;
  }
`;

const ThumbnailsContainer = styled.div`
  // display: flex;
  // overflow: hidden;
  // height: 64px;
  // padding-top: 7px;

  width: auto;
  height: 64px;
  border-radius: 15px;
  border-width: 1px;

  display: flex;
  align-items: center;
  justify-content: start;
  flex: 1;
  overflow: hidden;

  background: rgba(255, 255, 255, 0.5);

  &:last-child {
    border-top-right-radius: 15px !important;
    border-bottom-right-radius: 15px;
  }
`;

const TrimOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const OverlayLeft = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  background: rgba(255, 255, 255, 0.5);
`;

const OverlayRight = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  background: rgba(255, 255, 255, 0.5);
`;

const TrimHandle = styled.button`
  position: absolute;
  top: 0;
  width: 12px;
  height: 100%;
  background: transparent;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ew-resize;
  transition: all 0.2s;
`;

const TrimHandleLeft = styled(TrimHandle)`
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
`;

const TrimHandleRight = styled(TrimHandle)`
  right: 0;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
`;


const AudioTrack = styled.div`
  min-width: 320px;
  height: 35px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  border-radius: 10px;
  padding: 10px 12px;
  background: #0000000D;



  &>span{
    white-space: nowrap;
    height: 100%;
  }
  &>div{
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-items: center;
  }
`;

const QuestionLabel = styled.div`
  width: 80px;
  height: 22px;
  gap: 10px;
  padding: 4px 6px;
  border-radius: 8px;
  background: #00000099;
  position: absolute;
  top: 70px;
  z-index: 999999999999;
  color: #fff;
  border: 1px solid #fff;
  font-family: Figtree;
  font-weight: 700;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
  left: 20px;
`


const VideoTrimmer: React.FC<VideoTrimmerProps> = ({ isActive, width, index, duration,video, onTrimChange, onVideoTap, thumbnails,onTrimPlay }) => {
  const [startPos, setStartPos] = useState(0);
  const [endPos, setEndPos] = useState(0);
  const [isTriming, setIsTriming] = useState<boolean>(false);

  const [position, setPosition] = useState<{start?: number, end?: number}>({start: 0, end: duration});
  const containerRef = useRef(null);
  
  const [containerWidth, setContainerWidth] = useState(0);
  const [samplePlay, setSamplePlay] = useState(false);


  width = width > 50 ? 50 : width;
  useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setContainerWidth(rect.width);
    }
  }, [thumbnails]);

  const bindStartPos = useDrag(
    (state) => {
      const newStartPos = state.offset[0];
      setStartPos(newStartPos);
      onVideoTap?.(index);
      const newStartTime = parseFloat(((newStartPos / containerWidth) * Math.floor(duration)).toFixed(2));
      
      if (state.last) {
        setPosition({start: newStartTime,end:position?.end||duration})
        onTrimChange?.({startTime: newStartTime,endTime:position?.end||duration});
      }
    },
    {
      bounds: { left: 0, right: containerWidth + endPos },
    }
  );

  const bindEndPos = useDrag(
    (state) => {
      const newEndPos = state.offset[0];
      setEndPos(newEndPos);
      onVideoTap?.(index);
      const newEndTime = parseFloat((((containerWidth + newEndPos) / containerWidth) * Math.floor(duration)).toFixed(2));
      
      if (state.last) {
        setPosition({start: position?.start||0,end: newEndTime})
        onTrimChange?.({startTime: position?.start||0,endTime: newEndTime});
      }
    },
    {
      bounds: { left: -containerWidth + startPos, right: 0 },
    }
  );

  const handleTrimVideo =async()=>{
    return
    if(position.start === 0 && position.end===0) return;
    let editedDuration =position;

    setIsTriming(true)
    if (position.start < 0) {
      editedDuration.start = 0;
    }
    
    
    if (editedDuration.end > duration || editedDuration.end===0) {
      editedDuration.end = duration;
    }
    try{
      await onTrimChange?.({ startTime: editedDuration.start, endTime: editedDuration.end });
    }catch(error: any){
      console.error("Error trimming video")
    }finally{
      setIsTriming(false)
    }
    
  }
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (Array.isArray(thumbnails) && thumbnails.length > 0) {
        // onVideoTap?.(index);
      }
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, []);
  const handlePlayVideo=()=>{
    onTrimPlay({index,position})
    setSamplePlay(true)
  }
  const handlePauseVideo=()=>{
    onTrimPlay({index,position})
    setSamplePlay(false)
  }
  return (
    <Wrapper width={width}>
      <TrackItemHeader >
      {isTriming ? (
        <img src={Loader} title="Transcribing video" />
      ) : (
        <>
        {/* <FiCrop className={"tabButton"} onClick={handleTrimVideo} /> */}
        {samplePlay?(<FiPause className={"tabButton"} onClick={handlePauseVideo} />):<FiPlay className={"tabButton"} onClick={handlePlayVideo} />}
        
        </>
        
      )}
          
      </TrackItemHeader>
      <QuestionLabel>Question {index + 1}</QuestionLabel>
      <ThumbnailsContainer ref={containerRef}>{thumbnails}</ThumbnailsContainer>
      <AudioTrack>
      <VideoAudioWaveform status={samplePlay} video={video} position={position} width={width} />
      </AudioTrack>
      <TrimOverlay >
        {startPos > 0 && <OverlayLeft style={{ width: `${startPos}px` }}  />}
        {endPos < 0 && <OverlayRight style={{ width: `${Math.abs(endPos)}px` }} />}
        <TrimHandleLeft {...bindStartPos()} style={{ left: `${startPos}px` }}> | </TrimHandleLeft>
        <TrimHandleRight {...bindEndPos()} style={{ transform: `translateX(${endPos}px)` }}> | </TrimHandleRight>
      </TrimOverlay>
    </Wrapper>
  );
};

export default VideoTrimmer;


